import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode
} from 'react';

import { IBuyer, ISeller } from '~types/main';
import { safeJsonParse } from '@/src/utils/storage';

type TeamContextData = {
  buyer: IBuyer;
  setBuyer: (buyer: IBuyer) => void;
  updateBuyer: (buyer: Partial<IBuyer>) => void;
  seller: ISeller;
  setSeller: (seller: ISeller) => void;
  updateSeller: (seller: Partial<ISeller>) => void;
}

const TeamContext = createContext<TeamContextData>({} as TeamContextData);

type ProviderProps = {
  children: ReactNode;
}

export function TeamProvider({ children }: ProviderProps) {
  const [currentBuyer, setCurrentBuyer] = useState<IBuyer>(() => {
    const buyer = localStorage.getItem('@auge:buyer');
    return safeJsonParse<IBuyer>(buyer, {} as IBuyer);
  });

  const [currentSeller, setCurrentSeller] = useState<ISeller>(() => {
    const seller = localStorage.getItem('@auge:seller');
    return safeJsonParse<ISeller>(seller, {} as ISeller);
  });

  const updateBuyer = useCallback((buyer: Partial<IBuyer>) => {
    setCurrentBuyer(prev => {
      const updatedBuyer = { ...prev, ...buyer };
      localStorage.setItem('@auge:buyer', JSON.stringify(updatedBuyer));
      return updatedBuyer;
    });
  }, []);

  const handleSetBuyer = useCallback((updatedBuyer: IBuyer) => {
    localStorage.setItem('@auge:buyer', JSON.stringify(updatedBuyer));
    setCurrentBuyer(updatedBuyer);
  }, []);

  const updateSeller = useCallback((seller: Partial<ISeller>) => {
    setCurrentSeller(prev => {
      const updatedSeller = { ...prev, ...seller };
      localStorage.setItem('@auge:seller', JSON.stringify(updatedSeller));
      return updatedSeller;
    });
  }, []);

  const handleSetSeller = useCallback((updatedSeller: ISeller) => {
    localStorage.setItem('@auge:seller', JSON.stringify(updatedSeller));
    setCurrentSeller(updatedSeller);
  }, []);

  return (
    <TeamContext.Provider
      value={{
        buyer: currentBuyer,
        setBuyer: handleSetBuyer,
        updateBuyer,
        seller: currentSeller,
        setSeller: handleSetSeller,
        updateSeller,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);

  if (!context) {
    throw new Error("useTeam must be used within a TeamProvider.");
  }

  return context;
}
